import React, { useState, useEffect } from 'react';
import { Card, Button, message, Progress, Badge, Tag, Slider, InputNumber, Input, Select, DatePicker, TimePicker, Modal, Alert, Tooltip, Form } from 'antd';
import { HeartOutlined, HeartFilled, PlusOutlined, MinusOutlined, CalendarOutlined, ClockCircleOutlined, UserOutlined, EnvironmentOutlined, SaveOutlined, DollarOutlined, ExclamationCircleOutlined, ShoppingCartOutlined, CheckOutlined } from '@ant-design/icons';
import { getImageWithFallback, resolveImageSrc } from '../../utils/images';
import LocationSearch from '../UI/LocationSearch';
import dayjs from 'dayjs';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import api from '../../services/api';

function GetStarted({ initialCityOptions = [], onComplete, existingDraftId = null }) {
    const { user, requireAuth } = useAuth();
    const { updateEventDetails, updateServiceRequirements, openCart, cartItems, addToCart, removeFromCart, updateQuantity } = useCart();
    const [currentStep, setCurrentStep] = useState(0);
    const [data, setData] = useState({
        eventType: '',
        guestCount: 50,
        date: '',
        time: '',
        venue: '',
        selectedDishes: [],
        favoriteCategories: [],
        dietaryPreferences: [],
        services: [],
        specialRequests: '',
        contactPhone: '',
        contactEmail: ''
    });
    const [validationErrors, setValidationErrors] = useState({});
    const [isDraftSaving, setIsDraftSaving] = useState(false);
    const [showPriceEstimator, setShowPriceEstimator] = useState(false);
    const [priceEstimate, setPriceEstimate] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [draftId, setDraftId] = useState(null);

    const [eventTypes, setEventTypes] = useState([]);

    const [dishCategories, setDishCategories] = useState([]);
    const [dishesByCategory, setDishesByCategory] = useState({});

    const [services, setServices] = useState([]);
    const [dishSearch, setDishSearch] = useState('');
    const [loadingDraft, setLoadingDraft] = useState(false);

    const getFallbackImage = () => '/images/placeholder.svg';
    const buildImageForItem = (item) => {
        const src = getImageWithFallback(item, 'medium');
        const large = item?.imageVariants?.large || item?.variants?.large;
        const medium = item?.imageVariants?.medium || item?.variants?.medium;
        const original = item?.image || item?.featured_image || item?.url || item?.file_path;
        const srcSet = [
            medium ? `${resolveImageSrc(medium)} 600w` : null,
            large ? `${resolveImageSrc(large)} 1200w` : null,
            original ? `${resolveImageSrc(original)} 2000w` : null,
        ].filter(Boolean).join(', ');
        return { src, srcSet };
    };

    useEffect(() => {
        (async () => {
            try {
                const res = await api.getGetStartedMeta();
                if (res.success) {
                    setEventTypes(res.data.eventTypes || []);
                    setServices(res.data.services || []);
                    setDishCategories(res.data.categories || []);
                    setDishesByCategory(res.data.dishesByCategory || {});
                }
                // Prefill contact info from user if available
                if (user?.phone || user?.email) {
                    setData((prev) => ({
                        ...prev,
                        contactPhone: prev.contactPhone || user?.phone || '',
                        contactEmail: prev.contactEmail || user?.email || ''
                    }));
                }
            } catch (e) {
                // noop
            }
        })();
        // Restore saved get started state if available
        try {
            const saved = localStorage.getItem('get_started_state');
            if (saved) {
                const s = JSON.parse(saved);
                if (s?.form) setData((prev) => ({ ...prev, ...s.form }));
                if (typeof s?.step === 'number') setCurrentStep(s.step);
            }
        } catch {}
    }, []);

    // Restore draft from localStorage when user logs in
    useEffect(() => {
        if (user && !draftId) {
            try {
                const savedDraft = localStorage.getItem('get_started_draft');
                if (savedDraft) {
                    const { data: draftData, step: draftStep } = JSON.parse(savedDraft);
                    setData(draftData);
                    setCurrentStep(draftStep);
                    // Save to server and clear localStorage
                    saveDraft(true);
                    localStorage.removeItem('get_started_draft');
                }
            } catch {}
        }
    }, [user]);

    // Load existing cart items into selectedDishes if no saved state exists
    useEffect(() => {
        // Only load cart items if there's no saved get started state and no existing draft
        const hasSavedState = localStorage.getItem('get_started_state');
        const hasExistingDraft = existingDraftId;
        
        if (!hasSavedState && !hasExistingDraft && cartItems.length > 0) {
            // Convert cart items to selectedDishes format
            const cartDishes = cartItems.map(item => ({
                id: item.id,
                name: item.name,
                price: item.price,
                image: item.image,
                vendor_id: item.vendor_id,
                vendor_name: item.vendor_name,
                quantity: item.quantity,
                customizations: item.customizations
            }));
            
            setData(prev => ({
                ...prev,
                selectedDishes: cartDishes
            }));
        }
    }, [cartItems, existingDraftId]);

    // Load existing draft if provided
    useEffect(() => {
        if (existingDraftId && user) {
            loadExistingDraft(existingDraftId);
        }
    }, [existingDraftId, user]);

    const totalSteps = 5;
    const progressPercentage = ((currentStep + 1) / totalSteps) * 100;
    
    const loadExistingDraft = async (draftId) => {
        try {
            setLoadingDraft(true);
            const response = await api.getBookingDraft();
            if (response.success && response.data) {
                const draft = response.data;
                setDraftId(draft.id);
                setCurrentStep(draft.step || 0);
                setData({
                    eventType: draft.eventType || '',
                    guestCount: draft.guestCount || 50,
                    date: draft.date || '',
                    time: draft.time || '',
                    venue: draft.venue || '',
                    selectedDishes: draft.selectedDishes || [],
                    favoriteCategories: draft.favoriteCategories || [],
                    dietaryPreferences: draft.dietaryPreferences || [],
                    services: draft.services || [],
                    specialRequests: draft.specialRequests || '',
                    contactPhone: draft.contactPhone || (user?.phone || ''),
                    contactEmail: draft.contactEmail || (user?.email || '')
                });
                message.success('Draft loaded successfully');
            }
        } catch (error) {
            message.error('Failed to load draft');
            console.error('Error loading draft:', error);
        } finally {
            setLoadingDraft(false);
        }
    };

    const computeDishQuantity = (dish) => {
        const guestCount = data.guestCount || 10;
        const portionsPerPerson = dish.portions_per_person || 0.15;
        return Math.max(1, Math.ceil(guestCount * portionsPerPerson));
    };

    const toggleDish = (dish) => {
        const isSelected = data.selectedDishes.some(d => d.id === dish.id);
        if (isSelected) {
            // Remove from selected dishes and cart
            setData({
                ...data,
                selectedDishes: data.selectedDishes.filter(d => d.id !== dish.id)
            });
            removeFromCart(dish.id);
        } else {
            // Add to selected dishes and cart
            const dishWithQuantity = { ...dish, quantity: 1 };
            setData({
                ...data,
                selectedDishes: [...data.selectedDishes, dishWithQuantity]
            });
            addToCart(dish, 1);
        }
    };

    const updateDishQuantity = (dishId, quantity) => {
        const newQuantity = Math.max(1, quantity);
        setData({
            ...data,
            selectedDishes: data.selectedDishes.map(d => 
                d.id === dishId ? { ...d, quantity: newQuantity } : d
            )
        });
        
        // Also update cart quantity
        updateQuantity(dishId, newQuantity);
    };

    const toggleService = (service) => {
        const isSelected = data.services.includes(service.id);
        if (isSelected) {
            setData({
                ...data,
                services: data.services.filter(s => s !== service.id)
            });
        } else {
            setData({
                ...data,
                services: [...data.services, service.id]
            });
        }
    };

    const saveDraft = async (silent = false) => {
        if (!user) {
            // Save to localStorage for unauthenticated users
            try {
                localStorage.setItem('get_started_draft', JSON.stringify({
                    data,
                    step: currentStep,
                    last_saved: new Date().toISOString()
                }));
                if (!silent) message.success('Draft saved locally');
            } catch (error) {
                if (!silent) message.error('Failed to save draft');
            }
            return;
        }
        
        setIsDraftSaving(true);
        try {
            const payload = {
                ...data,
                step: currentStep,
                last_saved: new Date().toISOString()
            };
            
            const response = draftId 
                ? await api.updateBookingDraft(draftId, payload)
                : await api.createBookingDraft(payload);
                
            if (response.success) {
                if (!draftId) setDraftId(response.data.id);
                if (!silent) message.success('Draft saved successfully');
            }
        } catch (error) {
            if (!silent) message.error('Failed to save draft');
        } finally {
            setIsDraftSaving(false);
        }
    };

    const calculatePriceEstimate = () => {
        // Base price per person based on event type
        const basePrices = {
            'wedding': 800,
            'birthday': 500,
            'corporate': 600,
            'anniversary': 700,
            'baby-shower': 450,
            'other': 500
        };
        
        const basePrice = basePrices[data.eventType] || 500;
        let totalEstimate = basePrice * data.guestCount;
        
        // Add service costs
        const servicePrices = {
            'decoration': data.guestCount * 100,
            'photography': 15000,
            'music': 8000,
            'transport': data.guestCount * 50
        };
        
        data.services.forEach(serviceId => {
            totalEstimate += servicePrices[serviceId] || 0;
        });
        
        // Add dish quantity multiplier
        const dishMultiplier = data.selectedDishes.reduce((sum, dish) => sum + dish.quantity, 0) / data.selectedDishes.length || 1;
        totalEstimate *= Math.max(1, dishMultiplier * 0.3 + 0.7);
        
        return {
            basePrice: basePrice * data.guestCount,
            servicesCost: data.services.reduce((sum, serviceId) => sum + (servicePrices[serviceId] || 0), 0),
            dishMultiplier: dishMultiplier,
            total: Math.round(totalEstimate),
            perPerson: Math.round(totalEstimate / data.guestCount)
        };
    };

    const computeStepErrors = (step) => {
        const errors = {};
        
        switch (step) {
            case 0:
                if (!data.eventType) errors.eventType = 'Please select an event type';
                break;
            case 1:
                if (!data.venue.trim()) errors.venue = 'Venue location is required';
                if (!data.date) errors.date = 'Event date is required';
                if (!data.time) errors.time = 'Event time is required';
                if (data.guestCount < 10) errors.guestCount = 'Minimum 10 guests required';
                if (data.date && dayjs(data.date).isBefore(dayjs().add(2, 'day'))) {
                    errors.date = 'Event date must be at least 2 days in advance';
                }
                break;
            case 2:
                if (data.selectedDishes.length === 0) errors.selectedDishes = 'Please select at least one dish';
                const totalDishQuantity = data.selectedDishes.reduce((sum, dish) => sum + computeDishQuantity(dish), 0);
                const minRequiredQuantity = Math.ceil(data.guestCount * 0.1); // At least 10% of guest count
                if (totalDishQuantity < minRequiredQuantity) {
                    errors.selectedDishes = `Please select more dishes for ${data.guestCount} guests (minimum ${minRequiredQuantity} portions needed)`;
                }
                break;
            case 3:
                // Services are optional, no validation needed
                break;
            case 4:
                if (!data.contactPhone) {
                    errors.contactPhone = 'Please enter a contact phone';
                }
                if (!data.contactEmail) {
                    errors.contactEmail = 'Please enter a contact email';
                }
                if (data.specialRequests.length > 500) {
                    errors.specialRequests = 'Special requests must be under 500 characters';
                }
                break;
        }
        
        return errors;
    };

    const canProceed = () => {
        const errs = computeStepErrors(currentStep);
        return Object.keys(errs).length === 0;
    };

    const nextStep = () => {
        const errs = computeStepErrors(currentStep);
        setValidationErrors(errs);
        if (Object.keys(errs).length === 0 && currentStep < totalSteps - 1) {
            setCurrentStep(currentStep + 1);
            saveDraft(true); // Auto-save on step change
        }
    };

    const prevStep = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleComplete = async () => {
        const errs = computeStepErrors(currentStep);
        setValidationErrors(errs);
        if (Object.keys(errs).length > 0) return;
        
        // Update cart with event details
        updateEventDetails({
            date: data.date ? dayjs(data.date).format('YYYY-MM-DD') : null,
            time: data.time ? dayjs(data.time).format('HH:mm') : null,
            venue: data.venue,
            occasion: data.eventType,
            guestCount: data.guestCount,
            dietaryPreferences: {
                vegetarian: data.dietaryPreferences.includes('vegetarian') ? Math.ceil(data.guestCount * 0.6) : 0,
                nonVegetarian: data.dietaryPreferences.includes('non-vegetarian') ? Math.ceil(data.guestCount * 0.4) : 0,
                vegan: data.dietaryPreferences.includes('vegan') ? Math.ceil(data.guestCount * 0.1) : 0,
                specialDietary: data.dietaryPreferences.filter(p => !['vegetarian', 'non-vegetarian', 'vegan'].includes(p))
            },
            specialInstructions: data.specialRequests
        });

        // Update service requirements
        updateServiceRequirements({
            setup: data.services.includes('setup'),
            serving: data.services.includes('serving'),
            cleanup: data.services.includes('cleanup'),
            staffCount: data.services.includes('staff') ? Math.ceil(data.guestCount / 20) : 0,
            equipment: data.services.filter(s => ['tables', 'crockery', 'decorations', 'sound'].includes(s))
        });
        
        setIsSubmitting(true);
        try {
            const payload = {
                ...data,
                timestamp: new Date().toISOString(),
                status: 'pending_quotation',
                draft_id: draftId
            };
            // If not logged in, save state and redirect to login, then return to get-started
            if (!user) {
                try {
                    localStorage.setItem('get_started_state', JSON.stringify({ form: data, step: currentStep }));
                } catch {}
                const qs = new URLSearchParams({
                    menu_id: '',
                    city_id: '',
                }).toString();
                requireAuth('submit_booking', { getStarted: true }, `/get-started${qs ? `?${qs}` : ''}`);
                return;
            }
            
            const response = await api.createBookingFromGetStarted(payload);
            if (response.success) {
                message.success('Booking request submitted successfully!');
                // Draft is already handled by the backend
                setDraftId(null); // Clear the draft ID since it's been processed
                try { localStorage.removeItem('get_started_state'); } catch {}
                const bookingId = response?.data?.booking?.id || response?.data?.id;
                if (bookingId) {
                    window.location.href = `/booking-confirmation?booking_id=${bookingId}`;
                } else {
                    window.location.href = '/booking-confirmation';
                }
            } else {
                message.error('Failed to submit booking request');
            }
        } catch (error) {
            message.error('Failed to submit booking request');
        } finally {
            setIsSubmitting(false);
        }
    };

    const showEstimator = () => {
        if (data.selectedDishes.length === 0) {
            message.warning('Please select some dishes first');
            return;
        }
        const estimate = calculatePriceEstimate();
        setPriceEstimate(estimate);
        setShowPriceEstimator(true);
    };

    const renderStep = () => {
        switch (currentStep) {
            case 0:
                return (
                    <div className="space-y-6">
                        <div>
                            <h3 className="text-2xl font-bold text-[#2C2C2C] mb-2 flex items-center gap-2">
                                What's the occasion?
                                <span className="text-xl">🎉</span>
                            </h3>
                            <p className="text-sm text-[#4A4A4A] mb-6">Choose your event type to get personalized recommendations</p>
                            {validationErrors.eventType && (
                                <Alert message={validationErrors.eventType} type="error" className="mt-4" />
                            )}
                        </div>
                        <div className="flex flex-wrap justify-center gap-4">
                            {eventTypes.slice(0, 5).map((event) => {
                                const isSelected = data.eventType === (event.slug || event.id);
                                return (
                                    <div
                                        key={event.slug || event.id}
                                        onClick={() => setData({ ...data, eventType: (event.slug || event.id) })}
                                        className={`relative cursor-pointer transition-all duration-300 rounded-xl border-2 p-4 min-w-[120px] flex flex-col items-center ${
                                            isSelected
                                                ? 'border-[#C97D60] bg-[#C97D60]/5 shadow-md'
                                                : 'border-[#E5E5E5] hover:border-[#C97D60]/50'
                                        }`}
                                    >
                                        {isSelected && (
                                            <div className="absolute -top-2 -right-2 w-6 h-6 bg-[#C97D60] rounded-full flex items-center justify-center">
                                                <CheckOutlined className="text-white text-xs" />
                                            </div>
                                        )}
                                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-3 ${
                                            isSelected ? 'bg-[#C97D60] text-white' : 'bg-[#F5F3F0] text-[#4A4A4A]'
                                        }`}>
                                            {getEventIcon(event.slug)}
                                        </div>
                                        <span className="text-sm font-medium text-[#2C2C2C] text-center">{event.name}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );

            case 1:
                return (
                    <div className="space-y-4 sm:space-y-6">
                        <div className="text-center">
                            <h3 className="text-xl sm:text-2xl font-bold text-slate-800 mb-2">Event Details 📅</h3>
                            <p className="text-sm sm:text-base text-slate-600">Tell us about your event specifics</p>
                        </div>
                        
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                            <Card className="p-6">
                                <div className="flex items-center mb-4">
                                    <UserOutlined className="text-orange-500 mr-2" />
                                    <span className="font-semibold">Guest Count</span>
                                </div>
                                <div className="space-y-4">
                                    <Slider
                                        min={10}
                                        max={500}
                                        value={data.guestCount}
                                        onChange={(value) => setData({ ...data, guestCount: value })}
                                        marks={{
                                            10: '10',
                                            100: '100',
                                            250: '250',
                                            500: '500+'
                                        }}
                                    />
                                    <div className="text-center">
                                        <InputNumber
                                            min={10}
                                            max={1000}
                                            value={data.guestCount}
                                            onChange={(value) => setData({ ...data, guestCount: value })}
                                            className="text-xl font-bold"
                                            size="large"
                                            status={validationErrors.guestCount ? 'error' : ''}
                                        />
                                        {validationErrors.guestCount && (
                                            <div className="text-red-500 text-sm mt-1">{validationErrors.guestCount}</div>
                                        )}
                                        <span className="ml-2 text-slate-600">guests</span>
                                    </div>
                                </div>
                            </Card>

                            <Card className="p-6">
                                <div className="flex items-center mb-4">
                                    <EnvironmentOutlined className="text-orange-500 mr-2" />
                                    <span className="font-semibold">Venue Location</span>
                                </div>
                                <LocationSearch
                                    placeholder="Enter venue address or area"
                                    value={data.venue}
                                    onChange={(val) => setData({ ...data, venue: val })}
                                    size="large"
                                    status={validationErrors.venue ? 'error' : ''}
                                />
                                {validationErrors.venue && (
                                    <div className="text-red-500 text-sm mt-1">{validationErrors.venue}</div>
                                )}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                    <div>
                                        <div className="mb-2 text-slate-700 font-medium">Contact Phone</div>
                                        <Input
                                            size="large"
                                            placeholder="Enter contact phone"
                                            value={data.contactPhone}
                                            onChange={(e) => setData({ ...data, contactPhone: e.target.value })}
                                            status={validationErrors.contactPhone ? 'error' : ''}
                                        />
                                        {validationErrors.contactPhone && (
                                            <div className="text-red-500 text-sm mt-1">{validationErrors.contactPhone}</div>
                                        )}
                                    </div>
                                    <div>
                                        <div className="mb-2 text-slate-700 font-medium">Contact Email</div>
                                        <Input
                                            size="large"
                                            type="email"
                                            placeholder="Enter contact email"
                                            value={data.contactEmail}
                                            onChange={(e) => setData({ ...data, contactEmail: e.target.value })}
                                            status={validationErrors.contactEmail ? 'error' : ''}
                                        />
                                        {validationErrors.contactEmail && (
                                            <div className="text-red-500 text-sm mt-1">{validationErrors.contactEmail}</div>
                                        )}
                                    </div>
                                </div>
                            </Card>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <Card className="p-6">
                                <div className="flex items-center mb-4">
                                    <CalendarOutlined className="text-orange-500 mr-2" />
                                    <span className="font-semibold">Event Date</span>
                                </div>
                                <DatePicker
                                    className="w-full"
                                    size="large"
                                    value={data.date ? dayjs(data.date) : null}
                                    onChange={(date) => { setData({ ...data, date: date ? date.format('YYYY-MM-DD') : '' }); }}
                                    disabledDate={(current) => current && current < dayjs().startOf('day')}
                                    status={validationErrors.date ? 'error' : ''}
                                />
                                {validationErrors.date && (
                                    <div className="text-red-500 text-sm mt-1">{validationErrors.date}</div>
                                )}
                            </Card>

                            <Card className="p-6">
                                <div className="flex items-center mb-4">
                                    <ClockCircleOutlined className="text-orange-500 mr-2" />
                                    <span className="font-semibold">Event Time</span>
                                </div>
                                <TimePicker
                                    className="w-full"
                                    size="large"
                                    value={data.time ? dayjs(data.time, 'HH:mm') : null}
                                    onChange={(time) => { setData({ ...data, time: time ? time.format('HH:mm') : '' }); }}
                                    format="HH:mm"
                                    status={validationErrors.time ? 'error' : ''}
                                />
                                {validationErrors.time && (
                                    <div className="text-red-500 text-sm mt-1">{validationErrors.time}</div>
                                )}
                            </Card>
                        </div>
                    </div>
                );

            case 2:
                return (
                    <div className="space-y-6">
                        <div className="text-center">
                            <h3 className="text-2xl font-bold text-slate-800 mb-2">Choose Your Menu 🍽️</h3>
                            <p className="text-slate-600">Select dishes for your event</p>
                            {validationErrors.selectedDishes && (
                                <Alert message={validationErrors.selectedDishes} type="error" className="mt-4" />
                            )}
                        </div>

                        {/* Dish search */}
                        <div className="mb-4">
                            <Input
                                size="large"
                                placeholder="Search dishes by name or description"
                                allowClear
                                value={dishSearch}
                                onChange={(e) => setDishSearch(e.target.value)}
                            />
                        </div>

                        {dishCategories.map((category) => (
                            <Card key={category.id} className="p-6">
                                <div className="flex items-center mb-4">
                                    <span className="text-2xl mr-3">{category.icon || '🍽️'}</span>
                                    <h4 className="text-xl font-semibold">{category.name}</h4>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {(dishesByCategory[category.slug] || [])
                                        .filter((dish) => {
                                            if (!dishSearch) return true;
                                            const q = dishSearch.toLowerCase();
                                            return (
                                                (dish.name || '').toLowerCase().includes(q) ||
                                                (dish.description || '').toLowerCase().includes(q)
                                            );
                                        })
                                        .map((dish) => {
                                        const isSelected = data.selectedDishes.some(d => d.id === dish.id);
                                        const selectedDish = data.selectedDishes.find(d => d.id === dish.id);
                                        const { src, srcSet } = buildImageForItem(dish);
                                        
                                        return (
                                            <div
                                                key={dish.id}
                                                className={`card-ui overflow-hidden group relative cursor-pointer transition-all duration-300 border-2 rounded-xl ${
                                                    isSelected 
                                                        ? 'ring-2 ring-orange-500 bg-gradient-to-br from-orange-50 to-red-50 border-orange-300 shadow-lg scale-105' 
                                                        : 'border-gray-200 hover:border-orange-300 hover:shadow-lg hover:scale-105'
                                                }`}
                                                onClick={() => toggleDish(dish)}
                                            >
                                                <div className="relative overflow-hidden">
                                                    {src ? (
                                                        <img
                                                            src={src}
                                                            srcSet={srcSet || undefined}
                                                            sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
                                                            alt={dish.name}
                                                            className="card-cover transition-transform duration-500 group-hover:scale-105"
                                                            style={{ filter: 'brightness(0.95) contrast(1.05) saturate(1.08)' }}
                                                            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = getFallbackImage(); }}
                                                        />
                                                    ) : (
                                                        <img
                                                            src={getFallbackImage()}
                                                            alt={dish.name}
                                                            className="card-cover"
                                                        />
                                                    )}

                                                    <div className="absolute top-4 left-4 right-4 flex justify-between items-start">
                                                        <span className="bg-white/90 backdrop-blur-sm text-orange-600 px-3 py-1 rounded-full text-sm font-semibold shadow-lg">
                                                            {category.name}
                                                        </span>
                                                        <div className="flex items-center gap-1">
                                                            {dish.is_spicy && (
                                                                <span className="bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full shadow-lg">
                                                                    <span role="img" aria-label="spicy">choi</span>
                                                                </span>
                                                            )}
                                                            <span className="bg-white/90 backdrop-blur-sm text-lg px-2 py-1 rounded-full shadow-lg">
                                                                {dish.is_vegetarian ? '🥬' : '🍗'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="p-3">
                                                    <h5 className="card-title mb-1 leading-tight">{dish.name}</h5>
                                                    {dish.description && (
                                                        <p className="card-sub mb-2 line-clamp-2 leading-relaxed">{dish.description}</p>
                                                    )}
                                                    {dish.preparation_time ? (
                                                        <div className="flex items-center text-[11px] text-slate-500 mb-2">
                                                            <span className="mr-2 text-orange-500">⏱️</span>
                                                            <span className="font-medium">{dish.preparation_time} min prep time</span>
                                                        </div>
                                                    ) : null}

                                                    <div className="flex items-center justify-between">
                                                        <span className="text-sm text-slate-600">Click to select</span>
                                                        {isSelected && (
                                                            <div className="text-xs text-slate-600">Qty: <span className="font-semibold">{computeDishQuantity(dish)}</span> (auto)</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </Card>
                        ))}

                        {data.selectedDishes.length > 0 && (
                            <Card className="p-6 bg-orange-50 border-orange-200">
                                <h4 className="font-semibold mb-4">Selected Items ({data.selectedDishes.length})</h4>
                                <div className="text-sm text-slate-600 mb-3">
                                    💡 Quantities are automatically calculated based on portions per person for {data.guestCount} guests
                                </div>
                                <div className="space-y-2">
                                    {data.selectedDishes.map((dish) => (
                                        <div key={dish.id} className="flex justify-between items-center">
                                            <div>
                                                <span className="font-medium">{dish.name}</span>
                                                <span className="text-slate-500 ml-2">x{computeDishQuantity(dish)}</span>
                                                <span className="text-xs text-slate-400 ml-2">
                                                    ({dish.portions_per_person || 0.15} portions/person)
                                                </span>
                                            </div>
                                            <span className="text-green-600 font-semibold">✓ Selected</span>
                                        </div>
                                    ))}
                                </div>
                            </Card>
                        )}
                    </div>
                );

            case 3:
                return (
                    <div className="space-y-6">
                        <div className="text-center">
                            <h3 className="text-2xl font-bold text-slate-800 mb-2">Additional Services 🎨</h3>
                            <p className="text-slate-600">Enhance your event with extra services (optional)</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {services.map((service) => {
                                const sid = service.slug || service.id;
                                const isSelected = data.services.includes(sid);
                                return (
                                    <Card
                                        key={sid}
                                        className={`cursor-pointer transition-all duration-300 ${
                                            isSelected 
                                                ? 'ring-2 ring-orange-500 bg-orange-50' 
                                                : 'hover:shadow-md'
                                        }`}
                                        onClick={() => toggleService({ id: sid })}
                                    >
                                        <div className="p-6 text-center">
                                            <div className="text-4xl mb-3">{service.emoji}</div>
                                            <h4 className="font-semibold text-lg mb-2">{service.name}</h4>
                                            <p className="text-sm text-slate-600">{service.description}</p>
                                        </div>
                                    </Card>
                                );
                            })}
                        </div>
                    </div>
                );

            case 4:
                return (
                    <div className="space-y-6">
                        <div className="text-center">
                            <h3 className="text-2xl font-bold text-slate-800 mb-2">Review Your Event 📋</h3>
                            <p className="text-slate-600">Review your selections and submit your booking request</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <Card className="p-6">
                                <h4 className="font-semibold mb-4">Event Details</h4>
                                <div className="space-y-3">
                                    <div className="flex justify-between">
                                        <span>Event Type:</span>
                                        <span className="font-semibold">{(eventTypes.find(e => (e.slug || e.id) === data.eventType) || {}).name}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Guests:</span>
                                        <span className="font-semibold">{data.guestCount}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Date:</span>
                                        <span className="font-semibold">{data.date}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Time:</span>
                                        <span className="font-semibold">{data.time}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Venue:</span>
                                        <span className="font-semibold">{data.venue}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Contact Phone:</span>
                                        <span className="font-semibold">{data.contactPhone || '-'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Contact Email:</span>
                                        <span className="font-semibold">{data.contactEmail || '-'}</span>
                                    </div>
                                </div>
                            </Card>

                            <Card className="p-6">
                                <h4 className="font-semibold mb-4">Selected Items</h4>
                                <div className="space-y-3">
                                    <div>
                                        <h5 className="font-medium text-slate-700 mb-2">Menu Items ({data.selectedDishes.length})</h5>
                                        {data.selectedDishes.map((dish) => (
                                            <div key={dish.id} className="flex justify-between text-sm py-1">
                                                <span>{dish.name}</span>
                                                <span className="font-semibold">x{dish.quantity}</span>
                                            </div>
                                        ))}
                                    </div>
                                    
                                    {data.services.length > 0 && (
                                        <div className="border-t pt-3">
                                            <h5 className="font-medium text-slate-700 mb-2">Additional Services ({data.services.length})</h5>
                                            {data.services.map((serviceId) => {
                                                const service = services.find(s => s.id === serviceId);
                                                return service ? (
                                                    <div key={serviceId} className="flex justify-between text-sm py-1">
                                                        <span>{service.name}</span>
                                                        <span className="text-green-600">✓</span>
                                                    </div>
                                                ) : null;
                                            })}
                                        </div>
                                    )}
                                </div>
                            </Card>
                        </div>

                        <Card className="p-6 bg-blue-50 border-blue-200">
                            <div className="space-y-4">
                                <h4 className="font-semibold text-blue-800">Special Requests (Optional)</h4>
                                <Input.TextArea
                                    rows={4}
                                    placeholder="Any special dietary requirements, decoration preferences, or other requests..."
                                    value={data.specialRequests}
                                    onChange={(e) => setData({ ...data, specialRequests: e.target.value })}
                                    status={validationErrors.specialRequests ? 'error' : ''}
                                    showCount
                                    maxLength={500}
                                />
                                {validationErrors.specialRequests && (
                                    <div className="text-red-500 text-sm mt-1">{validationErrors.specialRequests}</div>
                                )}
                            </div>
                        </Card>

                        <div className="text-center bg-gradient-to-r from-orange-50 to-yellow-50 p-6 rounded-lg border border-orange-200">
                            <div className="text-lg font-semibold text-slate-800 mb-2">
                                🎯 What happens next?
                            </div>
                            <p className="text-slate-600">
                                Our team will review your requirements and send you a detailed quotation within 24 hours. 
                                No payment required now - you'll receive pricing after we review your event details.
                            </p>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    // Icon mapping for event types
    const getEventIcon = (slug) => {
        const iconClass = "w-8 h-8";
        switch (slug) {
            case 'wedding':
                return <HeartOutlined className={iconClass} />;
            case 'corporate':
                return <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>;
            case 'birthday':
                return <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
            case 'graduation':
                return <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l0-9" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l0-5M12 19l-4-2M12 19l4-2" /></svg>;
            case 'social-gathering':
            case 'other':
                return <UserOutlined className={iconClass} />;
            default:
                return <UserOutlined className={iconClass} />;
        }
    };

    return (
        <div className="min-h-screen bg-[#FAF8F5] py-8 px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-4xl">
                {/* Header */}
                <div className="text-center mb-8">
                    <p className="text-sm font-medium text-[#C97D60] uppercase tracking-wider mb-2">GET STARTED</p>
                    <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#2C2C2C] mb-3" style={{ fontFamily: 'var(--font-serif)' }}>
                        Plan Your Perfect Event
                    </h1>
                    <p className="text-base md:text-lg text-[#4A4A4A]">
                        Answer a few questions to receive personalized menu recommendations
                    </p>
                </div>

                {/* Progress Indicator */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-[#2C2C2C]">Step {currentStep + 1} of {totalSteps}</span>
                        <span className="text-sm font-medium text-[#C97D60]">{Math.round(progressPercentage)}% Complete</span>
                    </div>
                    <Progress 
                        percent={progressPercentage} 
                        strokeColor="#C97D60" 
                        trailColor="#F5F3F0"
                        showInfo={false}
                        strokeWidth={6}
                        className="rounded-full"
                    />
                </div>

                {/* Step Content */}
                <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 sm:mb-8">
                    {renderStep()}
                </div>

                {/* Action Buttons - Hidden for cleaner UI */}
                {false && (
                    <div className="flex justify-center gap-2 sm:gap-4 mb-4 sm:mb-6">
                        <Tooltip title={user ? 'Save your progress' : 'Login to save drafts'}>
                            <Button 
                                icon={<SaveOutlined />} 
                                onClick={() => saveDraft(false)}
                                loading={isDraftSaving}
                                disabled={!user}
                                size="small"
                                className="text-xs sm:text-sm"
                            >
                                <span className="hidden sm:inline">Save Draft</span>
                                <span className="sm:hidden">Save</span>
                            </Button>
                        </Tooltip>
                    </div>
                )}

                {/* Navigation */}
                <div className="flex justify-between items-center pt-6 border-t border-gray-200">
                    <button
                        onClick={prevStep}
                        disabled={currentStep === 0}
                        className={`text-[#2C2C2C] font-medium transition-colors ${
                            currentStep === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:text-[#C97D60]'
                        }`}
                    >
                        Back
                    </button>

                    {currentStep < totalSteps - 1 ? (
                        <Button 
                            type="primary" 
                            onClick={nextStep}
                            disabled={!canProceed()}
                            size="large"
                            className="px-8 bg-[#C97D60] hover:bg-[#B8735A] border-none h-12 rounded-lg font-semibold"
                        >
                            Continue
                            <svg className="ml-2 h-5 w-5 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </Button>
                    ) : (
                        <Button 
                            type="primary" 
                            onClick={handleComplete}
                            loading={isSubmitting}
                            size="large"
                            className="px-8 bg-[#C97D60] hover:bg-[#B8735A] border-none h-12 rounded-lg font-semibold"
                        >
                            Submit Booking Request
                        </Button>
                    )}
                </div>

                {currentStep === totalSteps - 1 && (
                    <div className="mt-4 flex justify-center">
                        <Button
                            onClick={async () => {
                                try {
                                    const payload = {
                                        vendor_id: 1,
                                        guest_count: data.guestCount,
                                        items: [
                                            ...data.selectedDishes.map(d => ({ name: d.name, menu_id: d.id, quantity: Math.max(1, Math.ceil((data.guestCount || 10) / 10)) })),
                                            ...data.services.map(s => ({ name: s, service_id: null, quantity: 1 })),
                                        ]
                                    };
                                    const res = await api.createQuote(payload);
                                    if (res.success) {
                                        message.success('Quote requested successfully');
                                    } else {
                                        message.error('Failed to request quote');
                                    }
                                } catch (e) {
                                    message.error('Failed to request quote');
                                }
                            }}
                        >
                            Request Quote (No Pricing Yet)
                        </Button>
                                    </div>
                                )}

                {/* Price Estimator removed for user UI */}
            </div>
        </div>
    );
}

export default GetStarted;
